import type { StackProps } from '@chakra-ui/react'
import { Text } from '@chakra-ui/react'
import { HStack, Spacer, Stack, Separator } from '@chakra-ui/react'
import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import type { LexicalEditor } from 'lexical'
import debounce from 'lodash/debounce'
import type { ChangeEvent, ChangeEventHandler, FC } from 'react'
import { useEffect, useRef } from 'react'

import RichTextInput from '@app/next/forms/inline/richTextInput'
import ViewerFieldWrapper from '@app/next/forms/inline/support/viewerFieldWrapper'
import TextAreaInput from '@app/next/forms/inline/textAreaInput'
import ChannelInput from '@app/pages/reports/components/channelInput'
import type { DomainReport } from '@app/types'
import { useReportUpdateMutation } from '@graphql/queries'

type Props = StackProps & {
  report: DomainReport
}

const ReportContent: FC<Props> = ({ report, ...rest }) => {
  const summary = report?.summary
  const summaryRef = useRef<LexicalEditor>(null)
  const [, updateReport] = useReportUpdateMutation()

  // Update the summary when it changes in the background, so AI pipelines can update it.
  useEffect(() => {
    if (
      summary?.length > 0 &&
      summaryRef.current?.isEditable() &&
      // do not update if the editor is focused, as it makes editing it inline nearly impossible.
      summaryRef.current.getRootElement() !== document.activeElement
    ) {
      summaryRef.current.update(() => {
        $convertFromMarkdownString(summary, TRANSFORMERS)
      })
    }
  }, [summary])

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    updateReport({
      input: {
        reportId: report.id,
        [e.target.name]: e.target.value
      }
    })

  const handleSubmit: ChangeEventHandler<HTMLFormElement> = (e) => {
    const formData = new FormData(e.target.form)
    const input = Object.fromEntries(formData.entries())

    e.currentTarget.reset()

    return updateReport({
      input: {
        ...input,
        reportId: report.id
      }
    })
  }

  const debouncedSubmit = debounce((e) => handleSubmit(e), 200)
  const { updatedAt } = report
  const updated = new Date(updatedAt)

  return (
    <Stack gap={4} px={4} bgColor="bg" {...rest} separator={<Separator />}>
      <Stack borderBottom="1px" borderBottomColor="border">
        <HStack>
          <TextAreaInput
            domainObject={report}
            name="name"
            title={report.name}
            fontWeight="bold"
            wordBreak="break-word"
            fontSize="xl"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur()
              }
            }}
          />
          <Spacer />
        </HStack>

        <ViewerFieldWrapper value={report?.summary}>
          <RichTextInput domainObject={report} name="summary" placeholder="Summary..." ref={summaryRef} />
        </ViewerFieldWrapper>
      </Stack>
      <Stack gap={4}>
        <Text color="subtle" fontSize="xs">
          Last updated on{' '}
          {updated.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
          })}{' '}
          at {updated.toLocaleTimeString()}
        </Text>

        <form onChange={(e: ChangeEvent<HTMLFormElement>) => debouncedSubmit(e)}>
          <ChannelInput report={report} onChange={onChange} />
        </form>
      </Stack>
    </Stack>
  )
}

export default ReportContent
